import React, { type FC, memo, type ReactElement, useCallback, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { Modal } from "@heroui/react";
import { type GQL } from "@binale-tech/shared";

import { AnsichtView } from "@accounting/shared/AnsichtView";
import { ModuleAVMLinks } from "./ModuleAVMLinks";
import { ModulePDFLinks } from "./ModulePDFLinks";
import { AppRoutes, getRoute, ModuleSubGroups } from "@domain/routing/routeConstants";
import { ModuleBankLinks } from "./ModuleBankLinks";
import { ModuleCommonImportExportLinks } from "./ModuleCommonImportExportLinks";
import { ModuleCommonKontoControlLinks } from "./ModuleCommonKontoControlLinks";
import { ModuleDebLinks } from "./ModuleDebLinks";
import { ModuleERLinks } from "./ModuleERLinks";
import { ModuleFELinks } from "./ModuleFELinks";
import { ModuleKBLinks } from "./ModuleKBLinks";
import { ModuleLALinks } from "./ModuleLALinks";
import { ModulePOSLinks } from "./ModulePOSLinks";
import { ModuleUStVALinks } from "./ModuleUStVALinks";
import { ModuleInvoicingLinks } from "@app/components/header/ModuleMenu/ModuleInvoicingLinks";
import { ModuleDmsLinks } from "./ModuleDmsLinks";

const Index: FC = () => {
    const [settingsPk, setSettingsPk] = useState<GQL.IProductKey | null>(null);
    const onOpenSettings = useCallback((pk: GQL.IProductKey) => setSettingsPk(pk), []);
    const { pathname } = useLocation();

    // ModuleMenu lives in the Header, outside the route tree, so the active
    // module is resolved with matchPath instead of a descendant <Routes>.
    const moduleLinks: { path: string; element: ReactElement }[] = useMemo(
        () => [
            {
                path: getRoute(AppRoutes.invoiceModule, { subGroup: ModuleSubGroups.ER }) + "/*",
                element: <ModuleERLinks isAZ={false} onOpenSettings={onOpenSettings} />,
            },
            {
                path: getRoute(AppRoutes.invoiceModule, { subGroup: ModuleSubGroups.ErA }) + "/*",
                element: <ModuleERLinks isAZ={true} onOpenSettings={onOpenSettings} />,
            },
            { path: AppRoutes.debitor + "/*", element: <ModuleDebLinks onOpenSettings={onOpenSettings} /> },
            { path: AppRoutes.kassenbuch + "/*", element: <ModuleKBLinks onOpenSettings={onOpenSettings} /> },
            { path: AppRoutes.bank + "/*", element: <ModuleBankLinks onOpenSettings={onOpenSettings} /> },
            { path: AppRoutes.freieErfassung + "/*", element: <ModuleFELinks onOpenSettings={onOpenSettings} /> },
            { path: AppRoutes.pos + "/*", element: <ModulePOSLinks onOpenSettings={onOpenSettings} /> },
            { path: AppRoutes.paySlip + "/*", element: <ModuleLALinks onOpenSettings={onOpenSettings} /> },
            { path: AppRoutes.avm + "/*", element: <ModuleAVMLinks /> },
            { path: AppRoutes.manageAccount + "/*", element: <ModuleCommonKontoControlLinks /> },
            { path: AppRoutes.recordImportExport + "/*", element: <ModuleCommonImportExportLinks /> },
            { path: AppRoutes.ocr + "/*", element: <ModulePDFLinks /> },
            { path: AppRoutes.invoicing + "/*", element: <ModuleInvoicingLinks /> },
            { path: AppRoutes.UStVA + "/*", element: <ModuleUStVALinks /> },
            { path: AppRoutes.dms + "/*", element: <ModuleDmsLinks /> },
        ],
        [onOpenSettings],
    );

    const activeModuleLinks = moduleLinks.find(({ path }) => matchPath(path, pathname))?.element ?? null;

    const onOpenChange = useCallback((isOpen: boolean) => {
        if (!isOpen) {
            setSettingsPk(null);
        }
    }, []);

    return (
        <>
            {activeModuleLinks}
            <Modal.Backdrop isOpen={!!settingsPk} onOpenChange={onOpenChange}>
                <Modal.Container size="lg">
                    <Modal.Dialog className="w-250 max-w-250" aria-label="Module settings">
                        <Modal.CloseTrigger />
                        <Modal.Body>{settingsPk && <AnsichtView pk={settingsPk} />}</Modal.Body>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </>
    );
};

export const ModuleMenu = memo(Index);
